import React, { useEffect, useState } from "react";
import { api, money, statusBadge } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { SplitReceipt } from "../components/SplitReceipt";
import { SupplierPicker } from "../components/SupplierPicker";
import { PayReceiptModal } from "../components/PayReceiptModal";
import { DirectPurchaseButton } from "../components/DirectPurchase";
import { ShoppingCart, Truck, CheckCircle2, Package, Calendar, DollarSign, Check, X, ScanLine, Loader2, Plus, Trash2, ChevronRight, TrendingUp, TrendingDown, Wallet, ClipboardList } from "lucide-react";

const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none transition";

// Budget-vs-actual for the items already purchased in a PO (like-for-like comparison)
export const poBudget = (po) => {
  let estBought = 0, actBought = 0, pendingItems = 0, pendingEst = 0;
  (po.items || []).forEach((it) => {
    const est = (it.estimated_unit_price ?? it.unit_price ?? 0) * (it.quantity ?? 1);
    if (it.purchased) { estBought += est; actBought += (it.total ?? 0); }
    else { pendingItems += 1; pendingEst += est; }
  });
  return { estBought, actBought, variance: actBought - estBought, pendingItems, pendingEst };
};

const ReceiptScanner = ({ po, onDone }) => {
  const { t } = useLang();
  const [stage, setStage] = useState("idle"); // idle | scanning | review
  const [supplier, setSupplier] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]);
  const [tax, setTax] = useState(0);
  const [scanTotal, setScanTotal] = useState(0);
  const [photoUrl, setPhotoUrl] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStage("scanning");
    try {
      const up = await api.upload(file);
      setPhotoUrl(up.url || "");
      const res = await api.post(`/purchase-orders/${po.id}/scan-receipt`, { file_ids: [up.id] });
      setSupplier(res.supplier || po.supplier || "");
      if (res.date) setDate(res.date);
      setTax(res.tax || 0);
      setScanTotal(res.total || 0);
      setItems((res.items || []).map((i) => ({ ...i })));
      setStage("review");
      if (!res.items?.length) toast.message(t("noItemsFound"));
    } catch { toast.error(t("error")); setStage("idle"); }
    finally { e.target.value = ""; }
  };

  const upd = (i, k, v) => setItems(items.map((it, j) => j === i ? { ...it, [k]: k === "description" ? v : parseFloat(v) || 0 } : it));
  const addRow = () => setItems([...items, { description: "", quantity: 1, unit_price: 0 }]);
  const del = (i) => setItems(items.filter((_, j) => j !== i));
  const itemsSubtotal = items.reduce((s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const grandTotal = itemsSubtotal + (parseFloat(tax) || 0);

  const confirm = async () => {
    try {
      await api.post(`/purchase-orders/${po.id}/receipt-items`, {
        supplier, purchased_date: new Date(date).toISOString(), tax: parseFloat(tax) || 0,
        items: items.filter((i) => i.description.trim()),
        photos: photoUrl ? [photoUrl] : [],
      });
      toast.success(t("saved"));
      setStage("idle"); onDone();
    } catch { toast.error(t("error")); }
  };

  if (stage === "idle") {
    return (
      <label data-testid={`scan-receipt-${po.id}`} className="inline-flex items-center gap-1.5 text-xs font-medium border border-violet-300 text-violet-700 rounded-lg px-3 py-1.5 hover:bg-violet-50 cursor-pointer">
        <ScanLine className="w-3.5 h-3.5" /> {t("scanReceipt")}
        <input type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={onFile} />
      </label>
    );
  }
  if (stage === "scanning") {
    return <span className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-700"><Loader2 className="w-3.5 h-3.5 animate-spin" />{t("scanning")}</span>;
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-4 bg-black/40 backdrop-blur-sm" onClick={() => setStage("idle")}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h2 className="text-base font-semibold text-slate-900">{t("reviewReceipt")}</h2>
          <button onClick={() => setStage("idle")} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("supplier")}</label><SupplierPicker value={supplier} onChange={setSupplier} testId="receipt-supplier" /></div>
            <div><label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("purchasedOn")}</label><input data-testid="receipt-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></div>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input data-testid={`receipt-desc-${i}`} value={it.description} onChange={(e) => upd(i, "description", e.target.value)} placeholder="Item" className={`${inputCls} col-span-6`} />
                <input type="number" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", e.target.value)} className={`${inputCls} col-span-2`} />
                <input type="number" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", e.target.value)} className={`${inputCls} col-span-3`} />
                <button onClick={() => del(i)} className="col-span-1 flex justify-center text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button onClick={addRow} className="text-xs font-medium text-violet-700 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" />{t("addItem")}</button>
          </div>
          <div className="border-t border-slate-200 pt-3 space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">{t("subtotal")}</span>
              <span className="font-medium text-slate-700 tabular-nums">{money(itemsSubtotal)}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <label className="text-sm text-slate-500">{t("tax")}</label>
              <input data-testid="receipt-tax" type="number" step="0.01" value={tax} onChange={(e) => setTax(parseFloat(e.target.value) || 0)} className="w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-right tabular-nums focus:ring-2 focus:ring-slate-900 outline-none" />
            </div>
            <div className="flex items-center justify-between text-base font-bold text-slate-900 pt-1.5 border-t border-slate-100">
              <span>{t("total")}</span>
              <span data-testid="receipt-grand-total" className="tabular-nums">{money(grandTotal)}</span>
            </div>
            {scanTotal > 0 && Math.abs(scanTotal - grandTotal) > 0.5 && (
              <p className="text-[11px] text-amber-600">{t("receiptTotalMismatch")}: {money(scanTotal)}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200 sticky bottom-0 bg-white">
          <button onClick={() => setStage("idle")} className="rounded-lg px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
          <button data-testid="receipt-confirm" onClick={confirm} className="rounded-lg px-4 py-2 text-sm font-medium bg-violet-600 text-white hover:bg-violet-700">{t("addToPo")}</button>
        </div>
      </div>
    </div>
  );
};

const PoItem = ({ po, item, onReload }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [price, setPrice] = useState(item.unit_price);
  const [supplier, setSupplier] = useState(item.supplier || po.supplier || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const savePurchase = async () => {
    try {
      await api.put(`/purchase-orders/${po.id}/items/${item.id}/purchase`, {
        actual_unit_price: parseFloat(price), supplier, purchased_date: new Date(date).toISOString(),
      });
      toast.success(t("saved"));
      setOpen(false); onReload();
    } catch { toast.error(t("error")); }
  };

  const removeItem = async () => {
    if (!window.confirm(t("confirmRemoveItem"))) return;
    try {
      await api.del(`/purchase-orders/${po.id}/items/${item.id}`);
      toast.success(t("deleted")); onReload();
    } catch { toast.error(t("error")); }
  };

  const variance = item.purchased && item.actual_unit_price != null
    ? (item.actual_unit_price - (item.estimated_unit_price ?? item.unit_price)) : null;

  return (
    <div data-testid={`po-item-${po.id}-${item.id}`} className="py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-3">
        {item.purchased
          ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          : <Package className="w-4 h-4 text-slate-300 shrink-0" />}
        <span className={`flex-1 text-sm ${item.purchased ? "text-slate-500" : "text-slate-700"}`}>{item.description}</span>
        <span className="text-xs text-slate-400 tabular-nums">{item.quantity} × {money(item.estimated_unit_price ?? item.unit_price)}</span>
        {item.purchased ? (
          <span className="text-sm font-medium text-emerald-700 w-20 text-right tabular-nums">{money(item.total)}</span>
        ) : (
          <button data-testid={`po-buy-${po.id}-${item.id}`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-medium bg-blue-600 text-white rounded-lg px-2.5 py-1 hover:bg-blue-700">
            <DollarSign className="w-3.5 h-3.5" />{t("markPurchased")}
          </button>
        )}
        <button data-testid={`po-del-item-${po.id}-${item.id}`} onClick={removeItem} title={t("delete")} className="shrink-0 text-slate-300 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>

      {item.purchased && (
        <div className="ml-7 mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[11px] text-slate-400">
          <span>{t("estimated")}: {money(item.estimated_unit_price ?? item.unit_price)}</span>
          <span className="text-slate-600 font-medium">{t("actual")}: {money(item.actual_unit_price)}</span>
          {variance != null && variance !== 0 && (
            <span className={variance > 0 ? "text-red-600" : "text-emerald-600"}>
              {variance > 0 ? "+" : ""}{money(variance)}
            </span>
          )}
          {item.supplier && <span>· {item.supplier}</span>}
          {item.purchased_date && <span>· {item.purchased_date.slice(0, 10)}</span>}
        </div>
      )}

      {open && !item.purchased && (
        <div className="ml-7 mt-2 grid grid-cols-12 gap-2 items-end bg-slate-50 rounded-lg p-2.5">
          <div className="col-span-3">
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("actualPrice")}</label>
            <input data-testid={`buy-price-${item.id}`} type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} />
          </div>
          <div className="col-span-4">
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("supplier")}</label>
            <SupplierPicker value={supplier} onChange={setSupplier} testId={`buy-supplier-${item.id}`} />
          </div>
          <div className="col-span-3">
            <label className="block text-[10px] font-bold uppercase text-slate-500 mb-1">{t("purchasedOn")}</label>
            <input data-testid={`buy-date-${item.id}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          <div className="col-span-2 flex gap-1">
            <button data-testid={`buy-save-${item.id}`} onClick={savePurchase} className="flex-1 inline-flex items-center justify-center bg-emerald-600 text-white rounded-lg py-2 hover:bg-emerald-700"><Check className="w-4 h-4" /></button>
            <button onClick={() => setOpen(false)} className="px-2 rounded-lg border border-slate-300 hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>
          </div>
        </div>
      )}
    </div>
  );
};

const AddPoItem = ({ po, onReload }) => {
  const { t } = useLang();
  const [desc, setDesc] = useState("");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);

  const add = async () => {
    if (!desc.trim()) return;
    try {
      await api.post(`/purchase-orders/${po.id}/items`, { description: desc.trim(), quantity: parseFloat(qty) || 1, unit_price: parseFloat(price) || 0 });
      toast.success(t("saved"));
      setDesc(""); setQty(1); setPrice(0); onReload();
    } catch { toast.error(t("error")); }
  };

  return (
    <div className="grid grid-cols-12 gap-2 items-center mt-2">
      <input data-testid={`po-newitem-desc-${po.id}`} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("addMaterial")} className={`${inputCls} col-span-6`} />
      <input data-testid={`po-newitem-qty-${po.id}`} type="number" step="0.01" value={qty} onChange={(e) => setQty(e.target.value)} placeholder={t("qty")} className={`${inputCls} col-span-2`} />
      <input data-testid={`po-newitem-price-${po.id}`} type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={t("unitPrice")} className={`${inputCls} col-span-3`} />
      <button data-testid={`po-newitem-add-${po.id}`} onClick={add} className="col-span-1 flex items-center justify-center bg-slate-900 text-white rounded-lg py-2 hover:bg-slate-800"><Plus className="w-4 h-4" /></button>
    </div>
  );
};

export default function Purchasing() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => api.get("/purchase-orders").then(setOrders).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = async (po, payload) => {
    try { await api.put(`/purchase-orders/${po.id}`, payload); toast.success(t("saved")); load(); }
    catch { toast.error(t("error")); }
  };

  const totals = orders.reduce((acc, po) => {
    const b = poBudget(po);
    acc.estimated += (po.estimated_total ?? po.total ?? 0);
    acc.actual += (po.actual_total || 0);
    acc.estBought += b.estBought;
    acc.actBought += b.actBought;
    if (b.pendingItems > 0 || po.status !== "delivered") { acc.pendingPOs += 1; acc.pendingEst += b.pendingEst; }
    return acc;
  }, { estimated: 0, actual: 0, estBought: 0, actBought: 0, pendingPOs: 0, pendingEst: 0 });
  const spentVariance = totals.actBought - totals.estBought;

  const cards = [
    { key: "budgeted", label: t("totalBudgeted"), value: money(totals.estimated), icon: ClipboardList, cls: "text-slate-900" },
    { key: "spent", label: t("totalSpent"), value: money(totals.actual), icon: Wallet, cls: "text-emerald-700" },
    { key: "variance", label: t("varianceOnBought"), value: `${spentVariance > 0 ? "+" : ""}${money(spentVariance)}`, icon: spentVariance > 0 ? TrendingUp : TrendingDown, cls: spentVariance > 0 ? "text-red-600" : "text-emerald-700" },
    { key: "pending", label: t("pendingPurchases"), value: totals.pendingPOs, sub: `${money(totals.pendingEst)} ${t("toBuy")}`, icon: ShoppingCart, cls: "text-amber-600" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{t("purchasing")}</h1>
          <p className="text-slate-500 text-sm mt-1">{t("poHint")}</p>
        </div>
        <div className="flex items-center gap-2">
          <DirectPurchaseButton onDone={load} />
          <SplitReceipt onDone={load} />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} data-testid={`purch-card-${c.key}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{c.label}</span>
                <Icon className={`w-4 h-4 ${c.cls}`} />
              </div>
              <div className={`text-2xl font-bold mt-1.5 tabular-nums ${c.cls}`}>{c.value}</div>
              {c.sub && <div className="text-[11px] text-slate-400 mt-0.5">{c.sub}</div>}
            </div>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-4 py-3">{t("po")}</th>
                <th className="text-left px-4 py-3">{t("title")}</th>
                <th className="text-center px-4 py-3">{t("materials")}</th>
                <th className="text-right px-4 py-3">{t("estimated")}</th>
                <th className="text-right px-4 py-3">{t("actual")}</th>
                <th className="text-right px-4 py-3">{t("variance")}</th>
                <th className="text-left px-4 py-3">{t("status")}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((po) => {
                const isOpen = expandedId === po.id;
                const boughtCount = po.items.filter((i) => i.purchased).length;
                const b = poBudget(po);
                const hasVar = boughtCount > 0 && Math.abs(b.variance) > 0.009;
                return (
                  <React.Fragment key={po.id}>
                    <tr data-testid={`po-row-${po.id}`} onClick={() => setExpandedId(isOpen ? null : po.id)} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer ${isOpen ? "bg-slate-50" : ""}`}>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        <div className="flex items-center gap-1.5">
                          <ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                          {po.number}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{po.title}</div>
                        <div className="text-xs text-slate-400">{t("forJob")}: {po.unit_name}</div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-600 tabular-nums">{boughtCount}/{po.items.length}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">{money(po.estimated_total ?? po.total)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium text-emerald-700">{money(po.actual_total || 0)}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {hasVar ? (
                          <span className={b.variance > 0 ? "text-red-600" : "text-emerald-600"} data-testid={`po-variance-${po.id}`}>{b.variance > 0 ? "+" : ""}{money(b.variance)}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadge(po.status)}`}>{t(po.status)}</span></td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`po-expanded-${po.id}`} className="border-b border-slate-200 bg-slate-50/70">
                        <td colSpan={7} className="px-6 py-4">
                          <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 flex items-center gap-1.5">
                            <Package className="w-3.5 h-3.5" />{po.estimate_number} · {t("materials")}
                          </div>
                          <div className="bg-white border border-slate-200 rounded-lg px-4 py-1">
                            {po.items.map((it, i) => <PoItem key={it.id || i} po={po} item={{ ...it, id: it.id || `idx-${i}` }} onReload={load} />)}
                          </div>
                          <AddPoItem po={po} onReload={load} />
                          {(po.tax_total || 0) > 0 && (
                            <div className="flex justify-end gap-6 text-xs text-slate-500 mt-2 px-1">
                              <span>{t("tax")}: <b className="text-slate-700 tabular-nums">{money(po.tax_total)}</b></span>
                              <span>{t("actual")}: <b className="text-emerald-700 tabular-nums">{money(po.actual_total || 0)}</b></span>
                            </div>
                          )}

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                            <div>
                              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">{t("supplier")}</label>
                              <SupplierPicker value={po.supplier || ""}
                                testId={`po-supplier-${po.id}`}
                                onChange={(v) => v !== (po.supplier || "") && update(po, { supplier: v })} />
                            </div>
                            <div>
                              <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1"><Calendar className="w-3 h-3 inline mr-1" />{t("deliveryDate")}</label>
                              <input data-testid={`po-delivery-${po.id}`} type="date" defaultValue={po.delivery_date ? po.delivery_date.slice(0, 10) : ""} onBlur={(e) => e.target.value && update(po, { delivery_date: e.target.value })} className={inputCls} />
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-200">
                            {po.status === "pending" && (
                              <button data-testid={`po-order-${po.id}`} onClick={() => update(po, { status: "ordered" })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg px-3 py-1.5 hover:bg-blue-700"><ShoppingCart className="w-3.5 h-3.5" />{t("markOrdered")}</button>
                            )}
                            {po.status === "ordered" && (
                              <button data-testid={`po-deliver-${po.id}`} onClick={() => update(po, { status: "delivered" })} className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg px-3 py-1.5 hover:bg-emerald-700"><Truck className="w-3.5 h-3.5" />{t("markDelivered")}</button>
                            )}
                            {po.status === "delivered" && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="w-4 h-4" />{t("delivered")}</span>
                            )}
                            {po.status === "delivered" && !po.paid && (
                              <PayPOButton po={po} onDone={load} />
                            )}
                            {po.paid && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-lg px-2 py-1"
                                    data-testid={`po-paid-badge-${po.id}`}>
                                <Wallet className="w-3.5 h-3.5" /> Paid · {money(po.paid_amount || 0)}
                              </span>
                            )}
                            <ReceiptScanner po={po} onDone={load} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {orders.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">
                  <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-slate-300" />{t("noData")}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* PayPOButton — thin wrapper around <PayReceiptModal> for a delivered PO.    */
/* -------------------------------------------------------------------------- */
function PayPOButton({ po, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid={`po-pay-${po.id}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs font-medium bg-slate-900 text-white rounded-lg px-3 py-1.5 hover:bg-slate-800"
      >
        <Wallet className="w-3.5 h-3.5" /> Pay
      </button>
      {open && (
        <PayReceiptModal
          entity="purchase_order"
          id={po.id}
          number={po.number}
          defaultAmount={po.actual_total || po.total || 0}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onDone(); }}
        />
      )}
    </>
  );
}
